import { useCallback, useRef, useState } from "react";
import { Message } from "@scrile/api-provider/dist/api/MessagesProvider";
import providers from "lib/providers";
import { stripMetaPrefix } from "lib/messageMetadata";

interface GlobalSearchResult {
  message: Message;
  threadId: string;
}

interface GlobalSearchState {
  results: GlobalSearchResult[];
  loading: boolean;
  query: string;
  totalCount: number;
}

export default function useGlobalMessageSearch() {
  const [state, setState] = useState<GlobalSearchState>({
    results: [],
    loading: false,
    query: "",
    totalCount: 0,
  });
  const abortRef = useRef(0);

  const search = useCallback(async (query: string, threads: any[]) => {
    if (!query.trim()) {
      setState({ results: [], loading: false, query: "", totalCount: 0 });
      return;
    }

    const searchId = ++abortRef.current;
    setState((prev) => ({ ...prev, loading: true, query }));

    const q = query.toLowerCase();
    const allResults: GlobalSearchResult[] = [];

    // Search across all loaded threads by fetching messages from each
    for (const thread of threads) {
      if (abortRef.current !== searchId) return;
      try {
        const response = await providers.MessagesProvider.find({
          data: { threadId: String(thread.id), sort: "TIME,DESC", page: 0, size: 50 },
        });
        const messages = response.result || [];
        for (const msg of messages) {
          const text = stripMetaPrefix(msg.text || "");
          if (text.toLowerCase().includes(q)) {
            allResults.push({ message: msg, threadId: String(thread.id) });
          }
        }
      } catch {
        // Skip inaccessible threads
      }
    }

    if (abortRef.current !== searchId) return;

    setState({
      results: allResults,
      loading: false,
      query,
      totalCount: allResults.length,
    });
  }, []);

  const clear = useCallback(() => {
    abortRef.current++;
    setState({ results: [], loading: false, query: "", totalCount: 0 });
  }, []);

  return {
    globalResults: state.results,
    globalLoading: state.loading,
    globalQuery: state.query,
    globalTotalCount: state.totalCount,
    globalSearch: search,
    clearGlobalSearch: clear,
  };
}
